<template>
  <div class="min-h-screen flex flex-col">
    <NavBar :title="' - UCO Collection'" />

    <section class="flex-1 bg-gradient-to-br from-blue-500 to-slate-800 pt-16 pb-4">
      <div class="max-w-lg mx-auto px-4 py-4">
        <!-- Route Context Banner -->
        <div v-if="routeContext" class="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-center gap-3 text-blue-700 text-sm">
          <i class="fas fa-route"></i>
          <span>Route collection — Stop {{ routeContext.stopSequence }}</span>
        </div>

        <!-- QR Scanning Stage -->
        <div v-if="!validated">
          <!-- QR Scanner -->
          <div v-if="!scanned" class="bg-white/95 backdrop-blur-lg rounded-2xl p-4 md:p-6 shadow-xl">
            <div class="text-center mb-6">
              <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl text-3xl text-white mb-4">
                <i class="fas fa-qrcode"></i>
              </div>
              <h2 class="text-xl font-semibold text-gray-800 mb-1">Scan Supplier Code</h2>
              <p class="text-sm text-gray-500">Position the QR code within the frame</p>
            </div>
            <div class="relative rounded-xl overflow-hidden bg-black">
              <qr-stream @decode="onDecode">
                <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div class="w-48 h-48 border-3 border-white/80 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.5)] animate-pulse-border"></div>
                </div>
              </qr-stream>
            </div>
          </div>

          <!-- Validation Stage -->
          <div v-else class="space-y-4">
            <div class="bg-white/95 backdrop-blur-lg rounded-2xl p-4 md:p-6 shadow-xl">
              <div class="flex items-center gap-4 mb-4 pb-4 border-b border-gray-200">
                <div class="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-2xl">
                  <i class="fas fa-check-circle"></i>
                </div>
                <h2 class="text-lg font-semibold text-gray-800">QR Code Scanned</h2>
              </div>
              <div class="space-y-3">
                <div class="flex justify-between items-center py-2">
                  <span class="text-sm text-gray-500">Supplier ID</span>
                  <span class="font-semibold text-gray-800 font-mono bg-gray-100 px-3 py-1 rounded-md">{{ supplierId }}</span>
                </div>
                <div class="flex justify-between items-center py-2">
                  <span class="text-sm text-gray-500">Location ID</span>
                  <span class="font-semibold text-gray-800 font-mono bg-gray-100 px-3 py-1 rounded-md">{{ locationId }}</span>
                </div>
              </div>
            </div>

            <!-- Error Message -->
            <transition name="error-fade">
              <div v-if="errorMessage" class="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                <i class="fas fa-exclamation-triangle"></i>
                {{ errorMessage }}
              </div>
            </transition>

            <!-- Action Buttons -->
            <div class="flex flex-col gap-3">
              <button
                @click="validateQR"
                :disabled="loading || errorMessage"
                class="w-full h-13 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="loading" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                <template v-else>
                  <i class="fas fa-check"></i>
                  <span>Validate Supplier</span>
                </template>
              </button>
              <button
                v-if="errorMessage"
                @click="resetScan"
                class="w-full h-13 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all active:scale-[0.98]"
              >
                <i class="fas fa-redo"></i>
                <span>Scan Again</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Collection Form -->
        <div v-if="validated" class="bg-white/95 backdrop-blur-lg rounded-2xl p-4 md:p-6 shadow-xl">
          <!-- Supplier Info Header -->
          <div class="flex flex-col md:flex-row items-center gap-4 mb-6 pb-4 border-b border-gray-200 text-center md:text-left">
            <div class="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-2xl text-white">
              <i class="fas fa-store"></i>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-gray-800">{{ supplierInfo.supplierName }}</h3>
              <p v-if="supplierInfo.locationName" class="text-sm text-gray-500">
                <i class="fas fa-map-marker-alt mr-1"></i>{{ supplierInfo.locationName }}
              </p>
            </div>
          </div>

          <!-- Success Message -->
          <div v-if="successMessage" class="text-center py-8">
            <div class="text-6xl text-green-600 mb-4 animate-scale-in">
              <i class="fas fa-check-circle"></i>
            </div>
            <p class="text-gray-700 mb-6">{{ successMessage }}</p>
            <router-link
              to="/home"
              class="inline-flex items-center justify-center gap-2 px-6 h-12 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30"
            >
              <i class="fas fa-home"></i>
              <span>Return Home</span>
            </router-link>
          </div>

          <!-- Collection Form Fields -->
          <form v-else @submit.prevent="completeCollection" class="space-y-5">
            <!-- Drum Delivery Toggle -->
            <div class="bg-gray-50 p-4 rounded-xl">
              <label class="flex items-center gap-3 cursor-pointer">
                <div class="relative">
                  <input type="checkbox" v-model="newDrumDelivery" class="sr-only peer" />
                  <div class="w-14 h-7 bg-gray-300 rounded-full peer-checked:bg-blue-500 transition-colors"></div>
                  <div class="absolute left-0.5 top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform peer-checked:translate-x-7"></div>
                </div>
                <span class="font-semibold text-gray-700">New Drum Delivery</span>
              </label>
            </div>

            <!-- Quantity Field -->
            <div>
              <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <i class="fas fa-weight text-gray-500"></i> Quantity (Liters)
              </label>
              <input
                v-model="quantity"
                type="number"
                placeholder="Enter quantity"
                inputmode="numeric"
                min="0"
                step="1"
                class="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all hover:border-gray-300"
              />
            </div>

            <!-- Supervisor Name Field -->
            <div>
              <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <i class="fas fa-user-tie text-gray-500"></i> Supervisor Full Name
              </label>
              <input
                v-model="supervisorName"
                type="text"
                placeholder="Enter supervisor full name"
                autocomplete="name"
                class="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all hover:border-gray-300"
              />
            </div>

            <!-- Signature Field -->
            <div>
              <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <i class="fas fa-signature text-gray-500"></i> Supervisor Signature
              </label>

              <div
                class="rounded-xl overflow-hidden mb-3 min-h-[160px] md:min-h-[180px]"
                :class="signatureSaved ? 'border-2 border-solid border-green-500 bg-green-50' : 'border-2 border-dashed border-gray-300 bg-gray-50'"
              >
                <vue-signature-pad
                  v-if="!signatureSaved"
                  ref="signaturePadRef"
                  class="w-full h-[160px] md:h-[180px] bg-white"
                  :options="{ penColor: '#1f2937' }"
                ></vue-signature-pad>

                <img
                  v-if="signatureSaved"
                  :src="signatureImage"
                  alt="Saved Signature"
                  class="w-full h-[160px] md:h-[180px] object-contain bg-white"
                />
              </div>

              <div class="flex gap-2 flex-wrap">
                <button
                  type="button"
                  @click="clearSignature"
                  :disabled="signatureSaved"
                  class="flex-1 min-w-[80px] h-11 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  <i class="fas fa-eraser"></i>
                  <span>Clear</span>
                </button>
                <button
                  type="button"
                  @click="saveSignature"
                  :disabled="signatureSaved"
                  class="flex-1 min-w-[80px] h-11 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  <i class="fas fa-save"></i>
                  <span>Save</span>
                </button>
                <button
                  v-if="signatureSaved"
                  type="button"
                  @click="deleteSignature"
                  class="flex-1 min-w-[80px] h-11 flex items-center justify-center gap-2 border-2 border-red-500 text-red-500 font-medium rounded-lg hover:bg-red-50 transition-colors"
                >
                  <i class="fas fa-trash"></i>
                  <span>Delete</span>
                </button>
              </div>
            </div>

            <!-- Error Message -->
            <transition name="error-fade">
              <div v-if="errorMessage" class="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                <i class="fas fa-exclamation-triangle"></i>
                {{ errorMessage }}
              </div>
            </transition>

            <!-- Submit Button -->
            <button
              type="submit"
              :disabled="apiCallInProgress"
              class="w-full h-14 flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl shadow-lg shadow-green-500/30 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="apiCallInProgress" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              <template v-else>
                <i class="fas fa-check-double"></i>
                <span>Complete Collection</span>
              </template>
            </button>
          </form>
        </div>
      </div>
    </section>

    <footer-bar></footer-bar>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import NavBar from "@/components/NavBar.vue";
import { QrStream } from "vue3-qr-reader";
import { useStore } from "vuex";
import { VueSignaturePad } from "vue-signature-pad";
import FooterBar from "@/components/FooterBar.vue";
import { authHeaders, forceReLogin } from "@/composables/apiAuth";

export default {
  components: {
    NavBar,
    QrStream,
    VueSignaturePad,
    FooterBar,
  },
  setup() {
    const scanned = ref(false);
    const validated = ref(false);
    const supplierId = ref("");
    const locationId = ref("");
    const quantity = ref(0);

    const supplierInfo = ref({});
    const orderId = ref("");
    const errorMessage = ref(null);

    const signaturePadRef = ref(null);

    const store = useStore();

    const loading = ref(false);

    const supervisorName = ref("");
    const signatureImage = ref(null);
    const signatureSaved = ref(false);

    const newDrumDelivery = ref(false);

    const apiCallInProgress = ref(false);
    const successMessage = ref(null);

    // Route context from HomeView (if navigated from a route stop)
    const routeContext = computed(() => store.state.routeContext);

    // Clear route context when leaving
    onUnmounted(() => {
      store.commit("CLEAR_ROUTE_CONTEXT");
    });

    const resetScan = () => {
      scanned.value = false;
      errorMessage.value = null;
      supplierId.value = "";
      locationId.value = "";
    };

    function onDecode(data) {
      let fullURL = data;

      if (!data.startsWith("http")) {
        fullURL = "http://placeholder.com/" + data;
      }

      const url = new URL(fullURL);
      const params = url.searchParams;

      supplierId.value = params.get("supplierid");
      locationId.value = params.get("locationid");
      scanned.value = true;

      console.debug(supplierId.value, locationId.value);
    }

    const clearSignature = () => {
      if (signaturePadRef.value) {
        signaturePadRef.value.clearSignature();
      }
    };

    const saveSignature = () => {
      const { isEmpty, data } = signaturePadRef.value.saveSignature();
      if (!isEmpty) {
        signatureImage.value = data;
        signatureSaved.value = true;
      }
    };

    const deleteSignature = () => {
      signatureSaved.value = false;
      signatureImage.value = null;
    };

    // Fresh, high-accuracy fix for the completion location check.
    // maximumAge: 0 — never reuse a fix cached from the drive in;
    // timeout: 15 s — bounds the wait at sign-off (typically 1–3 s).
    const getCurrentLocation = () => {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Geolocation not supported"));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            try {
              resolve({
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
                capturedAt: new Date(pos.timestamp).toISOString(),
              });
            } catch (e) {
              reject(e);
            }
          },
          (err) => reject(err),
          { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
        );
      });
    };

    // Sign-off must never hang on the device's geolocation stack: race the
    // fix against a hard 20 s ceiling (the native timeout is 15 s).
    const LOCATION_WATCHDOG_MS = 20000;
    const getCurrentLocationWithWatchdog = () =>
      Promise.race([
        getCurrentLocation(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("location watchdog timeout")), LOCATION_WATCHDOG_MS)
        ),
      ]);

    // Ask for location permission while the driver is still setting up, not
    // at the moment of signing. Result discarded; failure ignored.
    onMounted(() => {
      getCurrentLocation().catch(() => {});
    });

    const validateQR = async () => {
      loading.value = true;

      const API_URL = store.state.api + "verifySupplier";

      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            supplierId: supplierId.value,
            locationId: parseInt(locationId.value),
          }),
        });

        if (response.status === 401) {
          forceReLogin();
        }

        const result = await response.json();

        if (result[2] === 200 && result[1].valid) {
          supplierInfo.value = result[1].supplier;
          orderId.value = result[1].orderId;
          validated.value = true;
        } else {
          errorMessage.value = result[1].message || "Supplier not found or not scheduled for today.";
        }
      } catch (error) {
        errorMessage.value = "An error occurred during validation.";
      } finally {
        loading.value = false;
      }
    };

    const completeCollection = async () => {
      const truckNumber = sessionStorage.getItem("truckNumber");

      if (
        !truckNumber ||
        !supplierId.value ||
        !locationId.value ||
        !supervisorName.value ||
        !signatureImage.value ||
        (!newDrumDelivery.value && !quantity.value)
      ) {
        errorMessage.value =
          "Please ensure all fields are filled and signature is saved.";
        return;
      }

      apiCallInProgress.value = true;

      const payload = {
        supplierId: supplierId.value,
        locationId: parseInt(locationId.value),
        truckNumber: truckNumber,
        quantity: parseInt(quantity.value),
        supervisorName: supervisorName.value,
        supervisorSignature: signatureImage.value,
        orderId: orderId.value,
        drumDelivery: newDrumDelivery.value,
      };

      // Add route context for re-optimization if available
      if (routeContext.value) {
        payload.routeId = routeContext.value.routeId;
        payload.stopSequence = routeContext.value.stopSequence;
      }

      // Current device location: used by the API for the completion
      // location check (and route re-optimisation). A failed fix is not
      // fatal — the API records the order as "location not verified".
      try {
        const loc = await getCurrentLocationWithWatchdog();
        payload.lat = loc.lat;
        payload.lng = loc.lng;
        payload.accuracy = loc.accuracy;
        payload.capturedAt = loc.capturedAt;
      } catch (err) {
        console.warn("Could not get device location:", err?.message || err);
      }

      const API_URL = store.state.api + "completeUcoCollection";

      try {

        const response = await fetch(API_URL, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify(payload),
        });

        if (response.status === 401) {
          forceReLogin();
        }

        const result = await response.json();

        if (response.ok && result[0] === "Complete" && result[2] === 200) {
          if (newDrumDelivery.value) {
            successMessage.value = "Drum Delivery is recorded successfully.";
          } else {
            const odooPO = result[1].odooPurchaseOrderId;
            successMessage.value = `Collection for order ${result[1].orderId} recorded successfully.`
              + (odooPO ? ` Odoo PO #${odooPO} created.` : "");
          }
          errorMessage.value = null;
          console.log("Collection completed successfully:", result);
        } else {
          errorMessage.value =
            result.detail || "An error has occurred while completing the request, please contact operator.";
        }
      } catch (error) {
        errorMessage.value =
          "An error occurred while completing the collection.";
      } finally {
        apiCallInProgress.value = false;
      }
    };

    return {
      scanned,
      validated,
      supplierId,
      locationId,
      quantity,
      onDecode,
      validateQR,
      completeCollection,
      supplierInfo,
      errorMessage,
      loading,
      supervisorName,
      signatureImage,
      deleteSignature,
      signatureSaved,
      resetScan,
      clearSignature,
      saveSignature,
      signaturePadRef,
      newDrumDelivery,
      apiCallInProgress,
      successMessage,
      routeContext,
    };
  },
};
</script>

<style scoped>
.h-13 {
  height: 3.25rem;
}

.border-3 {
  border-width: 3px;
}

@keyframes pulse-border {
  0%, 100% {
    border-color: rgba(255, 255, 255, 0.8);
  }
  50% {
    border-color: rgba(59, 130, 246, 1);
  }
}

.animate-pulse-border {
  animation: pulse-border 2s ease-in-out infinite;
}

.animate-scale-in {
  animation: scaleIn 0.4s ease;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.error-fade-enter-active,
.error-fade-leave-active {
  transition: all 0.3s ease;
}

.error-fade-enter-from,
.error-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
